import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import SectionHead from "../ui/SectionHead";
import Reveal from "../ui/Reveal";
import Corners from "../ui/Corners";
import DeviceArt from "../ui/DeviceArt";
import {
  VENDORS,
  EDGE_DEVICES,
  CLOUD_PROVIDERS,
  HYBRID_PATTERNS,
  MODES,
} from "../../data/hardware";
import { Cpu, Cloud, Layers, Check, Signal, Lock } from "../ui/Icons";
import "./Deployment.css";

const MODE_ICON = { edge: Cpu, cloud: Cloud, hybrid: Layers };

const SPEC_ROWS = [
  ["compute", "AI compute"],
  ["accel", "Accelerator"],
  ["cpu", "CPU"],
  ["memory", "Memory"],
  ["power", "Power"],
  ["form", "Form factor"],
];

function firstDevice(vendorId) {
  const list = EDGE_DEVICES.filter((d) => d.vendor === vendorId);
  return (list.find((d) => d.recommended) || list[0])?.id;
}

function EdgePanel() {
  const [vendor, setVendor] = useState("nvidia");
  const [selected, setSelected] = useState(() => firstDevice("nvidia"));
  const devices = EDGE_DEVICES.filter((d) => d.vendor === vendor);
  const info = VENDORS.find((v) => v.id === vendor);
  const device = devices.find((d) => d.id === selected) || devices[0];

  const pickVendor = (id) => {
    setVendor(id);
    setSelected(firstDevice(id));
  };

  return (
    <>
      <div className="vtabs" role="tablist" aria-label="Hardware vendor">
        {VENDORS.map((v) => {
          const n = EDGE_DEVICES.filter((d) => d.vendor === v.id).length;
          const active = vendor === v.id;
          return (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`vtab ${active ? "is-active" : ""} ${v.id === "byod" ? "vtab--byod" : ""}`}
              onClick={() => pickVendor(v.id)}
            >
              {v.name}
              <span className="vtab__n mono">{n}</span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={vendor}
          className="vendor"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.22 }}
        >
          <p className="vendor__blurb">{info.blurb}</p>
          <div className="vendor__tools">
            <span className="mono vendor__label">toolchain</span>
            {info.tools.map((t) => (
              <span key={t} className="chip">
                {t}
              </span>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="dep__panel">
        <div>
          <p className="dep__hint mono">
            {vendor === "byod" ? "Select a server class" : "Select a target device"}
          </p>
          <div className="hw" role="radiogroup" aria-label="Edge hardware">
            {devices.map((d) => (
              <button
                key={d.id}
                type="button"
                role="radio"
                aria-checked={device.id === d.id}
                className={`hw__btn ${device.id === d.id ? "is-active" : ""}`}
                onClick={() => setSelected(d.id)}
              >
                <span className="hw__art">
                  <DeviceArt kind={d.art} image={d.image} alt="" {...(d.artProps || {})} />
                </span>
                <span className="hw__vendor mono">{d.tag}</span>
                <span className="hw__name">{d.name}</span>
                <span className="hw__spec mono">{d.compute}</span>
                {d.recommended && <span className="hw__badge mono">pick</span>}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.aside
            key={device.id}
            className="detail card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            aria-live="polite"
          >
            <div className={`detail__art ${device.image ? "detail__art--photo" : ""}`}>
              <Corners />
              <DeviceArt
                kind={device.art}
                image={device.image}
                alt={device.name}
                label={`${info.name} · ${device.name}`}
                {...(device.artProps || {})}
              />
              {device.credit && (
                <a
                  className="detail__credit mono"
                  href={device.credit.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Photo: {device.credit.author} · {device.credit.license}
                </a>
              )}
            </div>
            <div className="detail__head">
              <div>
                <span className="detail__vendor mono">{info.name}</span>
                <h3 className="h3">{device.name}</h3>
              </div>
              <span className="chip chip--accent detail__tag">{device.tag}</span>
            </div>
            <dl className="specs">
              {SPEC_ROWS.filter(([k]) => device[k]).map(([k, label]) => (
                <div key={k} className="specs__row">
                  <dt className="mono">{label}</dt>
                  <dd>{device[k]}</dd>
                </div>
              ))}
            </dl>
            <p className="detail__fit">
              <span className="mono detail__label">Typical fit</span>
              {device.fit}
            </p>
            <div className="chips">
              {device.best.map((b) => (
                <span key={b} className="chip">
                  {b}
                </span>
              ))}
            </div>
            <p className="detail__note mono">
              Vendor-published peak figures: INT8 unless stated, sparse where the vendor quotes
              it. Real throughput depends on the model, resolution and stream count; we benchmark
              on your hardware before committing.
            </p>
          </motion.aside>
        </AnimatePresence>
      </div>
    </>
  );
}

function CloudPanel() {
  return (
    <div className="dep__panel">
      <div className="providers">
        {CLOUD_PROVIDERS.map((p) => (
          <div key={p.id} className="provider card card--hover">
            <span className="provider__art">
              <DeviceArt kind={p.art} image={p.image} alt="" {...(p.artProps || {})} />
            </span>
            <h3 className="provider__name">{p.name}</h3>
            <p className="provider__note">{p.note}</p>
          </div>
        ))}
      </div>
      <aside className="detail card">
        <span className="detail__vendor mono">Managed inference cluster</span>
        <h3 className="h3">GPU-backed, autoscaled, centrally managed</h3>
        <p className="detail__fit">
          Streams are ingested over secure tunnels into a GPU cluster we operate in the
          cloud of your choice, or in your own account under your IAM policies.
        </p>
        <div className="chips">
          {["NVIDIA GPU instances", "Kubernetes", "Autoscaling", "Encrypted ingest", "Multi-region"].map(
            (c) => (
              <span key={c} className="chip">
                {c}
              </span>
            ),
          )}
        </div>
        <p className="detail__fit detail__residency">
          <Lock />
          <span>
            <span className="mono detail__label">Data residency</span>
            Pick the region. EU-only processing and storage is available for organisations
            that need it.
          </span>
        </p>
      </aside>
    </div>
  );
}

function HybridPanel() {
  return (
    <>
      <div className="dep__panel">
        <div className="hybrid card">
          <div className="hybrid__row">
            {[0, 1, 2].map((i) => (
              <div key={i} className="hybrid__node">
                <Cpu />
                <span className="mono">Edge node</span>
                <small>Detect · Track · Buffer</small>
              </div>
            ))}
          </div>
          <div className="hybrid__links" aria-hidden="true">
            <span className="hybrid__up mono">events · metadata · clips ↑</span>
            <span className="hybrid__down mono">↓ models · rules · updates</span>
          </div>
          <div className="hybrid__cloud">
            <Cloud />
            <div>
              <span className="mono">Cloud control plane</span>
              <small>Aggregate sites · retrain · dashboards · alerts · agents</small>
            </div>
            <Signal className="hybrid__signal" />
          </div>
        </div>
        <aside className="detail card">
          <span className="detail__vendor mono">Best of both</span>
          <h3 className="h3">Real-time on site, intelligence across sites</h3>
          <p className="detail__fit">
            Only what matters leaves the building. Edge nodes keep detecting through outages
            and sync when the link returns, while the cloud keeps every site consistent.
          </p>
          <div className="chips">
            {["Store-and-forward", "OTA model updates", "Low bandwidth", "Central dashboards"].map(
              (c) => (
                <span key={c} className="chip">
                  {c}
                </span>
              ),
            )}
          </div>
        </aside>
      </div>
      <div className="patterns">
        {HYBRID_PATTERNS.map((p, i) => (
          <div key={p.id} className="pattern card">
            <span className="pattern__idx mono">pattern 0{i + 1}</span>
            <h3 className="pattern__title">{p.title}</h3>
            <p className="pattern__text">{p.text}</p>
          </div>
        ))}
      </div>
    </>
  );
}

const PANEL = { edge: EdgePanel, cloud: CloudPanel, hybrid: HybridPanel };

/**
 * "Deploy anywhere" section. `compact` hides the section heading so it can be
 * embedded inside the Vision Platform page.
 */
export default function Deployment({ compact = false, id = "deploy" }) {
  const [mode, setMode] = useState("edge");
  const reduce = useReducedMotion();
  const Panel = PANEL[mode];
  const m = MODES[mode];

  return (
    <section id={id} className={`section dep ${compact ? "dep--compact" : ""}`}>
      <div className="container">
        {!compact && (
          <SectionHead
            eyebrow="Deploy anywhere"
            title="On the edge, in the cloud, or both."
            lead="Pick the topology that fits your latency, bandwidth and data-residency needs. Our models run on NVIDIA Jetson and other edge devices, on GPU clusters in the cloud, or in a hybrid of the two."
          />
        )}

        <Reveal>
          <div className="dep__tabs" role="tablist" aria-label="Deployment mode">
            {Object.values(MODES).map((opt) => {
              const Icon = MODE_ICON[opt.id];
              const active = mode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`dep__tab ${active ? "is-active" : ""}`}
                  onClick={() => setMode(opt.id)}
                >
                  {active && (
                    <motion.span
                      layoutId={`dep-pill-${id}`}
                      className="dep__pill"
                      transition={
                        reduce ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 32 }
                      }
                    />
                  )}
                  <Icon />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="dep__intro">
          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
            >
              <h3 className="h3">{m.title}</h3>
              <p className="dep__lead">{m.lead}</p>
              <ul className="check-list dep__features">
                {m.features.map((f) => (
                  <li key={f}>
                    <Check />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <Panel />
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
